import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Download, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const formatPercent = (value) => {
  return `${(value || 0).toFixed(2)}%`;
};

const PLReport = ({ plData, onExport }) => {
  if (!plData) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Funded</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(plData.summary?.total_funded)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Collected</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(plData.summary?.total_collected)}
            </div>
            {plData.summary?.total_refi_collected > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                <span className="text-green-600">{formatCurrency(plData.summary?.total_cash_collected || 0)}</span> Cash + 
                <span className="text-blue-600 ml-1">{formatCurrency(plData.summary?.total_refi_collected || 0)}</span> Refi
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Net P&L</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold flex items-center ${
              plData.summary?.net_pl >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {plData.summary?.net_pl >= 0 ? (
                <TrendingUp className="h-5 w-5 mr-2" />
              ) : (
                <TrendingDown className="h-5 w-5 mr-2" />
              )}
              {formatCurrency(plData.summary?.net_pl)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">ROI</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatPercent(plData.summary?.roi)}
            </div>
            {plData.summary?.total_refi_collected > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                Cash-only ROI: <span className="font-medium">{formatPercent(plData.summary?.roi_cash_only)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Monthly P&L Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={plData.monthly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="profit" stroke="#10b981" name="Profit" />
                <Line type="monotone" dataKey="loss" stroke="#ef4444" name="Loss" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>P&L by Funder</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={plData.by_funder || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="funder_name" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="net_pl" fill="#3b82f6" name="Net P&L" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Detailed P&L Breakdown</CardTitle>
          <div className="flex space-x-2">
            <Button size="sm" variant="outline" onClick={() => onExport('profit-loss', 'xlsx')}>
              <Download className="h-4 w-4 mr-1" />
              Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => onExport('profit-loss', 'pdf')}>
              <Download className="h-4 w-4 mr-1" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Funder</TableHead>
                <TableHead>Total Funded</TableHead>
                <TableHead>Collected</TableHead>
                <TableHead>Outstanding</TableHead>
                <TableHead>Profit/Loss</TableHead>
                <TableHead>ROI</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(plData.details || []).map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.funder_name}</TableCell>
                  <TableCell>{formatCurrency(item.funded)}</TableCell>
                  <TableCell>{formatCurrency(item.collected)}</TableCell>
                  <TableCell>{formatCurrency(item.outstanding)}</TableCell>
                  <TableCell className={item.pl >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatCurrency(item.pl)}
                  </TableCell>
                  <TableCell>{formatPercent(item.roi)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default PLReport;
